/**
 * Wrapper do Facebook Login for Business SDK pra Embedded Signup.
 *
 * Doc: https://developers.facebook.com/docs/whatsapp/embedded-signup/
 *
 * Em dev, se o `FB_APP_ID` ou `FB_CONFIG_ID` não estiverem setados, `launchEmbeddedSignup`
 * lança erro com mensagem instruindo a usar a conexão manual.
 */

declare global {
  interface Window {
    FB?: FacebookSDK;
    fbAsyncInit?: () => void;
  }
}

interface FacebookSDK {
  init: (config: {
    appId: string;
    autoLogAppEvents?: boolean;
    xfbml?: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: FBLoginResponse) => void,
    options?: {
      config_id: string;
      response_type: 'code';
      override_default_response_type?: boolean;
      extras?: Record<string, unknown>;
    },
  ) => void;
}

interface FBLoginResponse {
  authResponse?: {
    code: string;
  };
  status?: string;
}

interface MessageEventData {
  type?: string;
  /**
   * V4 do Embedded Signup pode retornar vários "tipos" de conclusão. Todos
   * começam com `FINISH` exceto ERROR/CANCEL. Vide doc Meta:
   * - FINISH: conclusão padrão da API de Nuvem (com phone_number_id + waba_id)
   * - FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: signup mantendo app móvel (Coexistence)
   * - FINISH_ONLY_WABA: cliente concluiu SEM número (não serve pro ChatPlus, precisa de número)
   * - FINISH_OBO_MIGRATION: migração "em nome de" (BSP legacy → Tech Provider)
   * - FINISH_GRANT_ONLY_API_ACCESS: apenas concessão de acesso à API
   * - ERROR: usuário encontrou erro durante o fluxo
   * - CANCEL: abandono OU erro user-reported (diferenciar por data.error_message vs data.current_step)
   */
  event?:
    | 'FINISH'
    | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
    | 'FINISH_ONLY_WABA'
    | 'FINISH_OBO_MIGRATION'
    | 'FINISH_GRANT_ONLY_API_ACCESS'
    | 'ERROR'
    | 'CANCEL'
    | string;
  data?: {
    // Conclusão bem-sucedida
    phone_number_id?: string;
    waba_id?: string;
    business_id?: string;
    ad_account_ids?: string[];
    page_ids?: string[];
    dataset_ids?: string[];
    catalog_ids?: string[];
    instagram_account_ids?: string[];
    waba_ids?: string[]; // multi-WABA flows
    // Abandono
    current_step?: string;
    // Erro user-reported
    error_message?: string;
    error_code?: string;
    session_id?: string;
    timestamp?: string;
  };
}

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
  /**
   * Tipo de evento de conclusão devolvido pelo Meta. Backend usa pra setar
   * whatsapp_numbers.coexistence_mode quando = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
   * (Story 5.16, lição #92).
   */
  finishEvent?: string;
}

const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const FB_GRAPH_VERSION = (import.meta.env.VITE_META_GRAPH_VERSION as string | undefined) ?? 'v20.0';

let sdkLoaded: Promise<void> | null = null;

function loadSdk(appId: string): Promise<void> {
  if (sdkLoaded) return sdkLoaded;
  sdkLoaded = new Promise((resolve, reject) => {
    if (window.FB) return resolve();

    window.fbAsyncInit = () => {
      window.FB?.init({ appId, autoLogAppEvents: true, xfbml: true, version: FB_GRAPH_VERSION });
      resolve();
    };

    const existing = document.getElementById('facebook-jssdk');
    if (existing) {
      // Outro código já injetou — esperar o init.
      const timeout = setTimeout(() => reject(new Error('FB SDK init timeout')), 10000);
      const interval = setInterval(() => {
        if (window.FB) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve();
        }
      }, 50);
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = FB_SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Falha ao carregar SDK do Facebook'));
    document.head.appendChild(script);
  });
  return sdkLoaded;
}

/**
 * Abre o popup Embedded Signup hosted pelo Meta. Resolve com `{code, waba_id, phone_number_id, business_id}`
 * quando o usuário completa o fluxo, ou rejeita se ele cancela / falha.
 */
export async function launchEmbeddedSignup(): Promise<EmbeddedSignupResult> {
  const appId = import.meta.env.VITE_META_APP_ID as string | undefined;
  const configId = import.meta.env.VITE_META_CONFIG_ID as string | undefined;

  if (!appId || !configId) {
    throw new EmbeddedSignupConfigError(
      'Embedded Signup não está configurado. Defina VITE_META_APP_ID + VITE_META_CONFIG_ID, ou use a conexão manual.',
    );
  }

  await loadSdk(appId);

  // Captura `phone_number_id` + `waba_id` via postMessage (canal complementar do popup).
  const messagePayload = await new Promise<{
    wabaId: string;
    phoneNumberId: string;
    businessId?: string;
    finishEvent?: string;
  }>(
    (resolve, reject) => {
      const handler = (event: MessageEvent): void => {
        // V4 spec: aceitar qualquer subdomínio facebook.com (cobre www, web, business, m, etc).
        const origin = event.origin;
        try {
          const url = new URL(origin);
          if (!url.hostname.endsWith('facebook.com')) return;
        } catch {
          return;
        }

        let parsed: MessageEventData;
        try {
          parsed = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }
        if (parsed?.type !== 'WA_EMBEDDED_SIGNUP') return;

        const evt = parsed.event ?? '';
        const data = parsed.data ?? {};

        // Conclusões bem-sucedidas — todos os FINISH_* além de FINISH.
        if (evt.startsWith('FINISH')) {
          window.removeEventListener('message', handler);
          // FINISH_ONLY_WABA: cliente concluiu sem número. ChatPlus precisa de
          // phone_number_id pra registrar via /register — rejeitar com erro claro.
          if (evt === 'FINISH_ONLY_WABA' || !data.phone_number_id) {
            reject(
              new EmbeddedSignupConfigError(
                'O fluxo terminou sem número de telefone (FINISH_ONLY_WABA). Adicione um número à WABA e tente novamente.',
              ),
            );
            return;
          }
          if (!data.waba_id) {
            reject(new Error('Popup retornou sem waba_id'));
            return;
          }
          resolve({
            wabaId: data.waba_id,
            phoneNumberId: data.phone_number_id,
            ...(data.business_id && { businessId: data.business_id }),
            // Story 5.16 — backend usa pra setar coexistence_mode quando
            // event === FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING
            finishEvent: evt,
          });
          return;
        }

        // Erro detectado pelo Meta durante o fluxo.
        if (evt === 'ERROR') {
          window.removeEventListener('message', handler);
          const msg = data.error_message ?? 'Erro durante o Cadastro Incorporado.';
          reject(new EmbeddedSignupConfigError(`${msg}${data.error_code ? ` (#${data.error_code})` : ''}`));
          return;
        }

        // CANCEL pode ser abandono real OU erro user-reported. Distinguir
        // pela presença de error_message no data.
        if (evt === 'CANCEL') {
          window.removeEventListener('message', handler);
          if (data.error_message) {
            reject(
              new EmbeddedSignupConfigError(
                `${data.error_message}${data.error_code ? ` (#${data.error_code})` : ''}`,
              ),
            );
          } else {
            reject(new EmbeddedSignupCancelledError());
          }
          return;
        }
      };
      window.addEventListener('message', handler);

      // Fallback timeout — usuário pode fechar a janela sem postMessage.
      setTimeout(() => {
        window.removeEventListener('message', handler);
      }, 15 * 60 * 1000);
    },
  );

  const codePayload = await new Promise<string>((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('FB SDK não inicializou'));
      return;
    }
    window.FB.login(
      (response) => {
        if (response.authResponse?.code) {
          resolve(response.authResponse.code);
        } else {
          reject(new EmbeddedSignupCancelledError());
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        // Padrão consolidado AtenderBem + ClickMassa em produção:
        // - setup: {} → sem prefill, cliente preenche no popup
        // - featureType: whatsapp_business_app_onboarding → ATIVA Coexistence
        //   (cliente mantém app móvel WhatsApp Business funcionando)
        // - sessionInfoVersion: '3' → payload de retorno V3 (mais novo)
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      },
    );
  });

  return {
    code: codePayload,
    wabaId: messagePayload.wabaId,
    phoneNumberId: messagePayload.phoneNumberId,
    ...(messagePayload.businessId && { businessId: messagePayload.businessId }),
    ...(messagePayload.finishEvent && { finishEvent: messagePayload.finishEvent }),
  };
}

export class EmbeddedSignupCancelledError extends Error {
  override readonly name = 'EmbeddedSignupCancelledError';
  constructor() {
    super('Conexão cancelada pelo usuário.');
  }
}

export class EmbeddedSignupConfigError extends Error {
  override readonly name = 'EmbeddedSignupConfigError';
}

/**
 * Opção alternativa de Embedded Signup: em vez de carregar o SDK do FB
 * inline no app, abre o Conector em meta.chatplusbr.com numa nova janela.
 * O Conector faz todo o fluxo (FB.login + exchange + persistência via API)
 * e responde via postMessage com o resultado.
 *
 * Vantagem: domínio do app principal não precisa estar em "Domínios
 * permitidos" do app Meta — só meta.chatplusbr.com precisa. Isso permite
 * testar em localhost (que Meta não aceita como domain).
 *
 * Pré-requisito: ticket (JWT do user logado) é passado via querystring pro
 * Conector usar como Bearer no POST /whatsapp-accounts/connect-embedded.
 */
export interface ConnectorResult {
  whatsapp_number_id: number;
  display_number: string;
  quality_rating: 'GREEN' | 'YELLOW' | 'RED' | null;
  messaging_tier: string | null;
  status: string;
}

export async function openMetaConnector(
  ticket: string,
  targetChannelId?: number,
  opts?: { migrate?: boolean },
): Promise<ConnectorResult> {
  // `||` (não `??`): build sem o arg deixa a var como STRING VAZIA, e
  // new URL('') estoura "Invalid URL" (lição #60 — vazio ≠ ausente).
  const baseUrl =
    (import.meta.env.VITE_META_CONECTOR_URL as string | undefined) || 'https://meta.chatplusbr.com';
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';
  // Resolve a URL absoluta da API pra passar pro Conector. Se for path relativo
  // (mesma origem em prod), expandimos com window.location.origin.
  const apiBaseAbs = apiBase.startsWith('http') ? apiBase : `${window.location.origin}${apiBase}`;
  const openerOrigin = window.location.origin;

  const url = new URL(baseUrl);
  url.searchParams.set('ticket', ticket);
  url.searchParams.set('api_base', apiBaseAbs);
  url.searchParams.set('opener_origin', openerOrigin);
  // Reconexão explícita: passa o ID do canal a ser reusado. Backend força
  // UPDATE preservando ID + histórico (troca phone/WABA/token). Sem isso,
  // backend cria canal novo.
  if (targetChannelId) url.searchParams.set('target_channel_id', String(targetChannelId));
  // Migração QR→Cloud: intenção explícita exigida pelo backend (TARGET_IS_QR).
  if (opts?.migrate) url.searchParams.set('migrate', '1');
  const appId = import.meta.env.VITE_META_APP_ID as string | undefined;
  const configId = import.meta.env.VITE_META_CONFIG_ID as string | undefined;
  const gv = import.meta.env.VITE_META_GRAPH_VERSION as string | undefined;
  if (appId) url.searchParams.set('app_id', appId);
  if (configId) url.searchParams.set('config_id', configId);
  if (gv) url.searchParams.set('graph_version', gv);

  const popup = window.open(url.toString(), 'chatplus-meta-conector', 'width=500,height=600');
  if (!popup) {
    throw new EmbeddedSignupConfigError(
      'Não foi possível abrir a janela do Conector. Verifique se o bloqueador de pop-ups está desabilitado.',
    );
  }

  return new Promise<ConnectorResult>((resolve, reject) => {
    let resolved = false;
    const cleanup = (): void => {
      window.removeEventListener('message', handler);
      clearInterval(pollClosed);
    };

    const handler = (event: MessageEvent): void => {
      // Só aceita mensagens do próprio Conector
      try {
        const conectorUrl = new URL(baseUrl);
        if (new URL(event.origin).hostname !== conectorUrl.hostname) return;
      } catch {
        return;
      }
      const data = event.data;
      if (!data || data.source !== 'chatplus-conector') return;
      if (data.type === 'success') {
        resolved = true;
        cleanup();
        resolve(data.result as ConnectorResult);
      } else if (data.type === 'cancelled') {
        resolved = true;
        cleanup();
        reject(new EmbeddedSignupCancelledError());
      } else if (data.type === 'error') {
        resolved = true;
        cleanup();
        reject(new EmbeddedSignupConfigError(data.message ?? 'Erro no Conector'));
      }
    };
    window.addEventListener('message', handler);

    // Se o user fechar a janela sem postMessage, considera cancelado.
    const pollClosed = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true;
        cleanup();
        reject(new EmbeddedSignupCancelledError());
      }
    }, 500);
  });
}
